/*
    Creation Time: 2024 - Aug - 16
    Created by:  (hamidrezakk)
    Maintainers:
       1.  HamidrezaKK (hamidrezakks@gmail.com)
       2.  Esra Cidem (esra.cidem@qlub.io)
    Auditor: HamidrezaKK
    Copyright Qlub_ 2024
*/

import React, { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { CloseRounded } from '@mui/icons-material';
import { IconButton, SwipeableDrawer } from '@mui/material';
import { Button, Divider, Typography } from '@qlub-dev/qlub-kit';
import AuthManager from '../../repository/auth';
import { getDefaultLanguage } from '../../utility/k_lang';
import useDefaultMenuItems from '../../hook/useDefaultMenuItems';
import { VisibilityEnum } from '../../repository/vendor';
import { useConfigContext } from '../config';
import { CloseDrawerFuncType, ILayoutContext, IMenuItem, RefreshFuncType } from './types';
import { pullToRefresh } from '../../const/events';
import { useQlubRouter } from '../../hook/router';
import { useTranslation } from '../../hook/translations';
import { useAuth } from '../auth';

import styles from './index.module.scss';

const LayoutContext = createContext<ILayoutContext>({
    menuItems: [],
    setMenuItems: () => console.log('not implemented'),
    widgets: null,
    setWidgets: () => console.log('not implemented'),
    pull: () => Promise.resolve('not implemented'),
    setPullFn: () => console.log('not implemented'),
    barRef: () => console.log('not implemented'),
    scrolled: false,
    enableScroll: () => console.log('not implemented'),
    back: () => console.log('not implemented'),
    setBackFn: () => console.log('not implemented'),
    forceBackVisibility: () => console.log('not implemented'),
    backIsVisible: undefined,
    setSwitchAccountOpenFn: () => console.log('not implemented'),
    setCloseDrawerFn: () => console.log('not implemented'),
    closeDrawer: () => console.log('not implemented'),
    loyalty: undefined,
    setLoyaltyProgram: () => console.log('not implemented'),
    footerVariant: '',
    setFooterVariant: () => console.log('not implemented'),
});

interface IProps {
    children?: ReactNode;
}

export function LayoutContextProvider({ children }: IProps) {
    const authManager = AuthManager.getInstance();
    const { t } = useTranslation('common');
    const { pathname, getRouterParam, back: routerBack, lang } = useQlubRouter();
    const { logout, isAuthorizedUser, profile } = useAuth();
    const { config } = useConfigContext();
    const defaultMenuItems = useDefaultMenuItems();
    const defaultLang = getDefaultLanguage(lang);

    useEffect(() => {
        if (defaultLang && profile?.id && profile?.defaultLang !== defaultLang) {
            authManager.setProfile({
                customerID: profile?.id,
                defaultLang,
            });
        }
    }, [defaultLang, profile?.id, profile?.defaultLang]);

    const pullableFn = useRef<any>(null);

    const pullHandler: RefreshFuncType = useCallback(() => {
        document.dispatchEvent(new CustomEvent(pullToRefresh));
        return pullableFn.current?.();
    }, []);

    const setPullFn = useCallback((fn: RefreshFuncType) => {
        pullableFn.current = fn;
    }, []);

    const closeDrawerFn = useRef<any>(null);

    const closeDrawerHandler: RefreshFuncType = useCallback(() => {
        return closeDrawerFn.current?.();
    }, []);

    const setCloseDrawerFn = useCallback((fn: CloseDrawerFuncType) => {
        closeDrawerFn.current = fn;
    }, []);

    const barRef = useRef<HTMLDivElement | null>(null);
    const [scrollEnabled, setScrollEnabled] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const [backIsVisible, setBackIsVisible] = useState<undefined | boolean>(undefined);

    const enableScrollHandler = useCallback((enable: boolean) => {
        setTimeout(() => {
            setScrollEnabled(enable);
        }, 500);
    }, []);

    const barRefHandler = useCallback((ref: any) => {
        barRef.current = ref;
    }, []);

    useEffect(() => {
        if (!scrollEnabled) {
            return () => {
                //
            };
        }

        const scrollDetector = () => {
            const top = barRef.current?.getBoundingClientRect().top || 0;
            if (top < (scrolled ? 20 : 0)) {
                setScrolled(true);
            } else {
                setScrolled(false);
            }
        };

        window.addEventListener('scroll', scrollDetector);
        return () => {
            window.removeEventListener('scroll', scrollDetector);
        };
    }, [scrollEnabled, scrolled]);

    const backFnRef = useRef<any>(null);

    const backHandler = useCallback(() => {
        if (backFnRef.current) {
            return backFnRef.current();
        }

        return routerBack();
    }, []);

    const forceBackVisibilityHandler = useCallback((visible: boolean) => {
        setBackIsVisible(visible);
    }, []);

    const setBackFnHandler = useCallback((fn: any) => {
        setTimeout(() => {
            backFnRef.current = fn;
        }, 500);
    }, []);

    const [menuItems, setMenuItems] = useState<IMenuItem[]>([]);
    const [widgets, setWidgets] = useState<React.ReactNode | null>(null);

    const [switchAccountOpen, setSwitchAccountOpen] = useState(false);

    const switchAccountHandler = useCallback(() => {
        setSwitchAccountOpen(true);
    }, []);

    const switchAccountCloseHandler = useCallback(() => {
        setSwitchAccountOpen(false);
    }, []);

    const modifiedMenuItems = useMemo<IMenuItem[]>(() => {
        if (!isAuthorizedUser) {
            return [...menuItems, ...defaultMenuItems];
        }

        return [
            ...menuItems,
            {
                href: getRouterParam('/receipts'),
                title: t('Payment receipts'),
                hidden: config?.paymentReceipts === VisibilityEnum.Hide,
            },
            {
                href: getRouterParam('/qr/[cc]/[slug]/[id]/[f1]/[f2]/[hash]/profile'),
                title: t('Account details'),
            },
            {
                onClick: switchAccountHandler,
                title: t('Switch account'),
            },
            ...defaultMenuItems,
        ].filter((o) => o?.hidden !== true);
    }, [switchAccountHandler, menuItems, isAuthorizedUser, lang]);

    const setMenuItemsHandler = useCallback((items: IMenuItem[]) => {
        setTimeout(() => {
            setMenuItems(items);
        }, 500);
    }, []);

    const setWidgetsHandler = useCallback((node: React.ReactNode | null) => {
        setWidgets(node);
    }, []);

    const [loyalty, setLoyalty] = useState<any>(undefined);

    const setLoyaltyProgramHandler = useCallback((value: any) => {
        setLoyalty(value);
    }, []);

    const [footerVariant, setFooterVariant] = useState('');

    const setFooterVariantHandler = useCallback((value) => {
        setFooterVariant(value);
    }, []);

    useEffect(() => {
        setScrollEnabled(false);
        setBackIsVisible(undefined);
        backFnRef.current = null;
        setMenuItems((item) => item.filter((o) => o.keepOnRouteChange) || []);
        setFooterVariant('');
    }, [pathname]);

    return (
        <LayoutContext.Provider
            value={{
                menuItems: modifiedMenuItems,
                setMenuItems: setMenuItemsHandler,
                widgets,
                setWidgets: setWidgetsHandler,
                pull: pullHandler,
                setPullFn,
                barRef: barRefHandler,
                scrolled,
                enableScroll: enableScrollHandler,
                back: backHandler,
                setBackFn: setBackFnHandler,
                forceBackVisibility: forceBackVisibilityHandler,
                backIsVisible,
                setSwitchAccountOpenFn: switchAccountHandler,
                closeDrawer: closeDrawerHandler,
                setCloseDrawerFn,
                loyalty,
                setLoyaltyProgram: setLoyaltyProgramHandler,
                footerVariant,
                setFooterVariant: setFooterVariantHandler,
            }}
        >
            <SwipeableDrawer
                anchor="bottom"
                open={switchAccountOpen}
                onClose={switchAccountCloseHandler}
                onOpen={switchAccountHandler}
                BackdropProps={{ sx: { background: '#00000099' } }}
                sx={{
                    zIndex: (theme) => theme.zIndex.drawer + 100,
                }}
                PaperProps={{
                    sx: {
                        maxWidth: '552px',
                        minHeight: '200px',
                        margin: '0 auto',
                        background: '#fff',
                        borderRadius: '30px 30px 0 0',
                    },
                }}
            >
                <div className={styles.container}>
                    <div className={styles.header}>
                        <div className={styles.top}>
                            <Typography typo="headline_sm" className={styles.title}>
                                {t('Stay With Qlub')}
                            </Typography>
                            <IconButton onClick={switchAccountCloseHandler} className={styles.close}>
                                <CloseRounded />
                            </IconButton>
                        </div>
                        <Typography typo="caption_overline" className={styles.subTitle}>
                            {t('Are you sure you want to log out?')}
                        </Typography>
                    </div>
                    <Divider />
                    <div className={styles.buttonGroup}>
                        <Button variant="contained" fullWidth onClick={logout}>
                            {t('Log out')}
                        </Button>
                        <Button variant="containedLight" fullWidth onClick={switchAccountCloseHandler}>
                            {t('Stay with Qlub')}
                        </Button>
                    </div>
                </div>
            </SwipeableDrawer>
            {children}
        </LayoutContext.Provider>
    );
}

export const useLayout = () => useContext(LayoutContext);
